/**
 * KeywordDMPage — Kommentar → DM (Keyword Automation)
 *
 * Layout:
 * 1. Neue Regel anlegen (Keyword + DM-Text + Aktiv-Schalter)
 * 2. Aktive Regeln verwalten (Toggle, Löschen via PATCH active=false)
 * 3. Aktivitäts-Log (dm_events) — EN-Status-Labels für Meta App Review
 */

import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  MessageSquareDot, Plus, RefreshCw, Loader2,
  ChevronDown, ChevronUp, CheckCircle2, XCircle,
  Clock, Send, SkipForward, AlertCircle, ToggleLeft, ToggleRight, Trash2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Status-Badge (EN für Meta-Reviewer) ─────────────────────────────────────
const STATUS = {
  received: { label: "received",  bg: "bg-slate-100",  text: "text-slate-600",  Icon: Clock          },
  matched:  { label: "matched",   bg: "bg-blue-100",   text: "text-blue-700",   Icon: CheckCircle2   },
  dm_sent:  { label: "dm_sent",   bg: "bg-green-100",  text: "text-green-700",  Icon: Send           },
  failed:   { label: "failed",    bg: "bg-red-100",    text: "text-red-700",    Icon: AlertCircle    },
  skipped:  { label: "skipped",   bg: "bg-amber-100",  text: "text-amber-700",  Icon: SkipForward    },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS[status] || STATUS.received;
  const Icon = cfg.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-sm font-mono font-medium ${cfg.bg} ${cfg.text}`}>
      <Icon size={13} />
      {cfg.label}
    </span>
  );
};

// ─── Formatierung ─────────────────────────────────────────────────────────────
const fmtTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

// ─── Aktivitäts-Log Zeile ─────────────────────────────────────────────────────
const EventRow = ({ ev }) => {
  const [open, setOpen] = useState(false);
  const hasRequest = ev.api_request && Object.keys(ev.api_request).length > 0;

  return (
    <div className="border border-stone-200 rounded-lg overflow-hidden">
      <div
        className="flex items-center gap-3 px-4 py-3 bg-white hover:bg-stone-50 cursor-pointer"
        onClick={() => hasRequest && setOpen(o => !o)}
      >
        {/* Zeit */}
        <span className="text-sm text-stone-500 w-36 shrink-0">{fmtTime(ev.created_at)}</span>

        {/* Status */}
        <span className="w-28 shrink-0"><StatusBadge status={ev.status} /></span>

        {/* Kommentartext */}
        <span className="text-sm text-stone-700 flex-1 truncate" title={ev.comment_text}>
          {ev.comment_text || <em className="text-stone-400">(leer)</em>}
        </span>

        {/* comment_id */}
        <span className="text-xs font-mono text-stone-400 w-32 shrink-0 truncate" title={ev.comment_id}>
          {ev.comment_id}
        </span>

        {/* Expand-Icon nur wenn api_request vorhanden */}
        <span className="w-5 shrink-0 text-stone-400">
          {hasRequest && (open ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
        </span>
      </div>

      {open && hasRequest && (
        <div className="px-4 py-3 bg-stone-900 border-t border-stone-700">
          <p className="text-xs text-stone-400 mb-2 font-mono">
            API Request (token redacted):
          </p>
          <pre className="text-sm text-green-300 font-mono whitespace-pre-wrap break-all leading-relaxed">
{`POST ${ev.api_request.url}

${JSON.stringify(ev.api_request.body, null, 2)}`}
          </pre>
          {ev.api_response_id && (
            <p className="text-xs text-stone-400 mt-2 font-mono">
              ✓ message_id: {ev.api_response_id}
            </p>
          )}
          {ev.error && (
            <p className="text-xs text-red-400 mt-2 font-mono">
              ✗ error: {ev.error}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Hauptseite ───────────────────────────────────────────────────────────────
export default function KeywordDMPage() {
  const [rules, setRules] = useState([]);
  const [events, setEvents] = useState([]);
  const [loadingRules, setLoadingRules] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(false);

  // Formular
  const [keyword, setKeyword] = useState("");
  const [dmText, setDmText] = useState("");
  const [formActive, setFormActive] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchRules = useCallback(async () => {
    setLoadingRules(true);
    try {
      const { data } = await axios.get(`${API}/keyword-rules`);
      setRules(data.rules || []);
    } catch (e) {
      toast.error("Regeln konnten nicht geladen werden");
    } finally {
      setLoadingRules(false);
    }
  }, []);

  const fetchEvents = useCallback(async () => {
    setLoadingEvents(true);
    try {
      const { data } = await axios.get(`${API}/dm-events?limit=100`);
      setEvents(data.events || []);
    } catch (e) {
      toast.error("Aktivitäts-Log konnte nicht geladen werden");
    } finally {
      setLoadingEvents(false);
    }
  }, []);

  useEffect(() => {
    fetchRules();
    fetchEvents();
  }, [fetchRules, fetchEvents]);

  const handleCreateRule = async (e) => {
    e.preventDefault();
    if (!keyword.trim() || !dmText.trim()) {
      toast.error("Keyword und DM-Text sind Pflichtfelder");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/keyword-rules`, {
        keyword: keyword.trim(),
        dm_text: dmText.trim(),
        active: formActive,
      });
      toast.success("Regel gespeichert");
      setKeyword("");
      setDmText("");
      setFormActive(true);
      fetchRules();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const toggleRule = async (rule) => {
    try {
      await axios.patch(`${API}/keyword-rules/${rule.id}`, { active: !rule.active });
      setRules(rs => rs.map(r => r.id === rule.id ? { ...r, active: !r.active } : r));
    } catch (e) {
      toast.error("Konnte Regel nicht ändern");
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-8">

      {/* Header */}
      <div className="flex items-start gap-3">
        <MessageSquareDot size={28} className="text-pink-500 mt-0.5 shrink-0" />
        <div>
          <h1 className="text-2xl font-bold text-stone-800">Kommentar → DM</h1>
          <p className="text-stone-500 mt-1">
            Wenn jemand einen bestimmten Begriff unter deinem Instagram-Post kommentiert,
            sendet die App automatisch eine private Antwort (DM) — genau einmal, policy-konform.
          </p>
        </div>
      </div>

      {/* ─── Neue Regel ─────────────────────────────────────────────────────── */}
      <section className="bg-white rounded-xl border border-stone-200 p-6">
        <h2 className="font-semibold text-stone-700 mb-4 flex items-center gap-2">
          <Plus size={18} /> Neue Regel
        </h2>
        <form onSubmit={handleCreateRule} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-stone-600 mb-1">
                Keyword <span className="text-red-500">*</span>
              </label>
              <input
                value={keyword}
                onChange={e => setKeyword(e.target.value)}
                placeholder='z. B. "Infos", "mehr erfahren"'
                className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-400"
              />
              <p className="text-xs text-stone-400 mt-1">
                Groß-/Kleinschreibung wird ignoriert. Substring-Match.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-600 mb-1">
                DM-Text <span className="text-red-500">*</span>
              </label>
              <textarea
                value={dmText}
                onChange={e => setDmText(e.target.value)}
                rows={3}
                placeholder="Hallo! Hier sind die Infos, die du angefragt hast: ..."
                className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-400 resize-none"
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => setFormActive(a => !a)}
              className="flex items-center gap-2 text-sm text-stone-600"
            >
              {formActive
                ? <ToggleRight size={22} className="text-green-500" />
                : <ToggleLeft size={22} className="text-stone-400" />}
              {formActive ? "Aktiv" : "Inaktiv"}
            </button>

            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 bg-pink-500 hover:bg-pink-600 disabled:opacity-60 text-white text-sm font-medium px-5 py-2 rounded-lg transition-colors"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
              Regel speichern
            </button>
          </div>
        </form>
      </section>

      {/* ─── Regel-Liste ────────────────────────────────────────────────────── */}
      <section className="bg-white rounded-xl border border-stone-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-stone-700">
            Regeln {!loadingRules && <span className="text-stone-400 font-normal">({rules.length})</span>}
          </h2>
          <button onClick={fetchRules} className="text-stone-400 hover:text-stone-600">
            <RefreshCw size={16} className={loadingRules ? "animate-spin" : ""} />
          </button>
        </div>

        {loadingRules ? (
          <div className="flex items-center gap-2 text-stone-400 text-sm py-4">
            <Loader2 size={16} className="animate-spin" /> Lade…
          </div>
        ) : rules.length === 0 ? (
          <p className="text-stone-400 text-sm py-4">Noch keine Regeln angelegt.</p>
        ) : (
          <div className="space-y-2">
            {rules.map(rule => (
              <div
                key={rule.id}
                className={`flex items-start gap-3 p-3 rounded-lg border ${rule.active ? "border-stone-200 bg-stone-50" : "border-stone-100 bg-stone-50 opacity-60"}`}
              >
                <button onClick={() => toggleRule(rule)} className="mt-0.5 shrink-0">
                  {rule.active
                    ? <ToggleRight size={22} className="text-green-500" />
                    : <ToggleLeft size={22} className="text-stone-400" />}
                </button>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-stone-800">
                    Keyword: <span className="font-mono bg-stone-200 px-1.5 py-0.5 rounded text-pink-700">{rule.keyword}</span>
                  </p>
                  <p className="text-sm text-stone-500 mt-0.5 truncate">{rule.dm_text}</p>
                </div>
                <button
                  onClick={() => toggleRule(rule)}
                  title={rule.active ? "Deaktivieren" : "Aktivieren"}
                  className="shrink-0 text-stone-300 hover:text-red-400 mt-0.5"
                >
                  {rule.active ? <XCircle size={18} /> : <CheckCircle2 size={18} className="text-green-400" />}
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ─── Aktivitäts-Log ──────────────────────────────────────────────────── */}
      <section className="bg-white rounded-xl border border-stone-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-semibold text-stone-700">
              Aktivitäts-Log {!loadingEvents && <span className="text-stone-400 font-normal">({events.length})</span>}
            </h2>
            <p className="text-xs text-stone-400 mt-0.5">
              Jeder empfangene Instagram-Kommentar — Klick auf eine Zeile zeigt den gesendeten API-Request.
            </p>
          </div>
          <button onClick={fetchEvents} className="text-stone-400 hover:text-stone-600">
            <RefreshCw size={16} className={loadingEvents ? "animate-spin" : ""} />
          </button>
        </div>

        {/* Spalten-Header */}
        <div className="hidden sm:flex items-center gap-3 px-4 py-2 text-xs font-medium text-stone-400 uppercase tracking-wide border-b border-stone-100 mb-1">
          <span className="w-36 shrink-0">Zeit</span>
          <span className="w-28 shrink-0">Status</span>
          <span className="flex-1">Kommentar</span>
          <span className="w-32 shrink-0">comment_id</span>
          <span className="w-5 shrink-0" />
        </div>

        {loadingEvents ? (
          <div className="flex items-center gap-2 text-stone-400 text-sm py-4">
            <Loader2 size={16} className="animate-spin" /> Lade…
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-10">
            <MessageSquareDot size={36} className="text-stone-300 mx-auto mb-2" />
            <p className="text-stone-400 text-sm">Noch keine Events.</p>
            <p className="text-xs text-stone-400 mt-1">
              Sobald ein Kommentar unter einem deiner Instagram-Posts eingeht,
              erscheint er hier.
            </p>
          </div>
        ) : (
          <div className="space-y-1.5">
            {events.map(ev => <EventRow key={ev.id} ev={ev} />)}
          </div>
        )}
      </section>
    </div>
  );
}
